import { useRef, useState } from "react";
import { asEntry, entryMeasure, type Exercise, type Measure } from "../core/exercises";
import { bestSet, type NewRecord } from "../core/progress";
import { groupByMonth, volume, type Session } from "../core/sessions";
import { setLabels } from "../core/templates";
import { APP_VERSION } from "../version";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { Badges } from "./ExercisePage";
import { formatClock, formatDay, formatKg, formatSet, t } from "./texts";

/**
 * The session's exercises with their sets merged when an exercise appears twice in the same variant (as the history
 * and records count them). `e` is the exercise in that variant; `variant` is set when it is not the glossary's.
 */
function exerciseGroups(s: Session, byId: Map<string, Exercise>) {
  const out: { key: string; exerciseId: string; e?: Exercise; measure?: Measure; variant?: Measure; sets: Session["exercises"][number]["sets"] }[] = [];
  for (const x of s.exercises) {
    const glossary = byId.get(x.exerciseId);
    const measure = glossary && entryMeasure(glossary, x);
    const key = `${x.exerciseId} ${measure ?? ""}`;
    const g = out.find((o) => o.key === key);
    if (g) g.sets = [...g.sets, ...x.sets];
    else out.push({ key, exerciseId: x.exerciseId, e: asEntry(glossary, x), measure, variant: measure !== glossary?.measure ? measure : undefined, sets: x.sets });
  }
  return out;
}

/** The records of one group (exercise and variant). */
const groupRecords = (records: NewRecord[], g: { exerciseId: string; measure?: Measure }) =>
  records.filter((r) => r.exerciseId === g.exerciseId && r.measure === g.measure);

/** Tab Verlauf as in Strong: finished workouts by month, newest first; a card shows the best set of each exercise. */
export function HistoryTab(props: {
  sessions: Session[];
  exercises: Exercise[];
  timeline: Map<string, NewRecord[]>;
  onOpen(id: string): void;
  /** five taps on the version within 3 s: the hidden motion recording screen (todo 7); opens only where there are sensors */
  onSecret?(): void;
}) {
  const versionTaps = useRef<number[]>([]);
  const tapVersion = () => {
    const now = Date.now();
    versionTaps.current = [...versionTaps.current.filter((x) => now - x < 3000), now];
    if (versionTaps.current.length >= 5) { versionTaps.current = []; props.onSecret?.(); }
  };
  const byId = new Map(props.exercises.map((e) => [e.id, e]));
  const groups = groupByMonth(props.sessions);
  return (
    <>
      <h1>{t.history.title}</h1>
      {groups.length === 0 && <p className="muted-text">{t.historyEmpty}</p>}
      {groups.map((g) => (
        <section key={g.key}>
          <header className="titlebar section-head">
            <h2>{t.history.month(g.year, g.month)}</h2>
            <span className="muted">{t.history.workouts(g.items.length)}</span>
          </header>
          <div className="cards">
            {g.items.map((s) => {
              const kg = volume(s.exercises);
              const prs = props.timeline.get(s.id)?.length ?? 0;
              return (
                <button key={s.id} className="card" onClick={() => props.onOpen(s.id)}>
                  <span className="name">{s.name}</span>
                  <span className="sub">{formatDay(s.startedAt)}</span>
                  <span className="sub facts-line">
                    <span>⏱ {formatClock(s.endedAt - s.startedAt)}</span>
                    {kg > 0 && <span>{formatKg(kg)}</span>}
                    {prs > 0 && <span className="pr">🏆 {t.history.prCount(prs)}</span>}
                  </span>
                  <span className="best-grid">
                    {exerciseGroups(s, byId).map((x) => {
                      const e = x.e;
                      const best = e && bestSet(x.sets, e);
                      return (
                        <span key={x.key} className="best-row">
                          <span>{t.history.exerciseLine(x.sets.length, variantName(e?.name, x.variant))}</span>
                          <span className="sub">{best ? formatSet(best, e) : ""}</span>
                        </span>
                      );
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      <footer className="muted"><button className="muted version-tap" onClick={tapVersion}>{t.version(APP_VERSION)}</button></footer>
    </>
  );
}

/** One finished workout: facts, notes, every set, the new records; repeat, edit, save as a template, delete. */
export function SessionPage(props: {
  session: Session;
  exercises: Exercise[];
  records: NewRecord[];
  /** a workout is running: "Wiederholen" would start a second one */
  busy: boolean;
  onBack(): void;
  onEdit(): void;
  onSaveAsTemplate(): Promise<string>;
  onRepeat(): void;
  onDelete(): Promise<void>;
}) {
  const s = props.session;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState<string>();
  useBack(props.onBack);
  const byId = new Map(props.exercises.map((e) => [e.id, e]));
  const kg = volume(s.exercises);

  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={props.onBack}>{t.back}</button>
      </header>
      <h1>{s.name}</h1>
      <dl className="facts">
        <dt>{t.summary.date}</dt><dd>{formatDay(s.startedAt)}</dd>
        <dt>{t.summary.duration}</dt><dd>{formatClock(s.endedAt - s.startedAt)}</dd>
        {kg > 0 && <><dt>{t.summary.volume}</dt><dd>{formatKg(kg)}</dd></>}
        {s.notes && <><dt>{t.run.notes}</dt><dd className="notes">{s.notes}</dd></>}
      </dl>
      {props.records.length > 0 && <p className="pr-head">🏆 {t.progress.newRecords(props.records.length)}</p>}
      {notice && <p className="notice">{notice}</p>}

      {exerciseGroups(s, byId).map((x) => {
        const e = x.e;
        const labels = setLabels(x.sets);
        return (
          <section key={x.key} className="ex-card">
            <div className="name ex-name">{variantName(e?.name, x.variant)}</div>
            <ol className="set-lines">
              {x.sets.map((set, i) => <li key={i}><span className={"set-tag " + set.type}>{labels[i]}</span>{formatSet(set, e)}</li>)}
            </ol>
            <Badges records={groupRecords(props.records, x)} />
          </section>
        );
      })}

      <div className="action-stack">
        <button className="primary" disabled={props.busy} onClick={props.onRepeat}>{t.history.repeat}</button>
        {props.busy && <p className="muted-text">{t.history.workoutRunning}</p>}
        <button className="secondary" onClick={props.onEdit}>{t.history.edit}</button>
        <button className="secondary" onClick={async () => setNotice(t.history.templateSaved(await props.onSaveAsTemplate()))}>{t.history.saveAsTemplate}</button>
        <button className="danger" onClick={() => setConfirmDelete(true)}>{t.history.delete}</button>
      </div>

      {confirmDelete && (
        <ConfirmDialog text={t.history.confirmDelete(s.name)} confirm={t.history.delete} danger
          onCancel={() => setConfirmDelete(false)} onConfirm={async () => { setConfirmDelete(false); await props.onDelete(); }} />
      )}
    </>
  );
}

/** "Sandbag Squat (Reps in Zeit)" for an exercise done in the variant that is not the glossary's (todo 16). */
export const variantName = (name: string | undefined, variant: Measure | undefined) =>
  (name ?? t.template.unknownExercise) + (variant ? ` (${t.measure(variant)})` : "");
